import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, subDays } from "date-fns";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { useAutoPageGuide } from "@/react/guides";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useClockingAccess } from "./hooks/useClockingAccess";
import { useClockingAttendance } from "./hooks/useClockingAttendance";
import { useClockingActions } from "./hooks/useClockingActions";
import { useClockingSettings } from "./hooks/useClockingSettings";
import { CustomMatrixClockingGrid } from "./grid/CustomMatrixClockingGrid";
import { ClockingToolbar, defaultClockingDateRange } from "./ClockingToolbar";
import { ClockingSettingsSheet } from "./ClockingSettingsSheet";
import { ClockingEmptyState } from "./ClockingEmptyState";
import { AttendanceDetailPanel } from "./detail/AttendanceDetailPanel";
import { TimeOffDetailPanel } from "./detail/TimeOffDetailPanel";
import { ExceptionInboxPanel } from "./inbox/ExceptionInboxPanel";
import {
  buildExceptionInbox,
  countActionableExceptions,
} from "./mappers/buildExceptionInbox";
import { TIME_OFF_APPROVAL } from "./status/displayStatusColors";
import type {
  AttendanceDetail,
  ClockingCell,
  ClockingGroup,
  ClockingGridSelection,
  ClockingRow,
  TimeOffDetail,
} from "./types";

type DetailState =
  | { kind: "attendance"; detail: AttendanceDetail }
  | { kind: "timeOff"; detail: TimeOffDetail }
  | null;

function DetailLoadingPane() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <div className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading details…</p>
    </div>
  );
}

export function ClockingView() {
  const { selectedBoard } = useSwivuBryntumContext();
  const boardId = selectedBoard?.id ?? selectedBoard?.record?.id ?? null;

  const { access, isLoading: accessLoading, error: accessError, refresh: refreshAccess } =
    useClockingAccess(boardId);

  const [range, setRange] = useState(defaultClockingDateRange);
  const [exceptionsOnly, setExceptionsOnly] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selection, setSelection] = useState<ClockingGridSelection | null>(null);
  const [detail, setDetail] = useState<DetailState>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxDateFilter, setInboxDateFilter] = useState<string | null>(null);
  const detailRequestRef = useRef(0);

  const canView = Boolean(access?.can_view_clocking);
  const canManage = Boolean(access?.can_manage_clocking);
  const clockingAvailable = access?.clocking_available !== false;

  const query = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      exceptionsOnly,
    }),
    [range.startDate, range.endDate, exceptionsOnly]
  );

  const {
    model,
    isLoading: attendanceLoading,
    error: attendanceError,
    refresh: refreshAttendance,
    applyAttendanceDetail,
  } = useClockingAttendance(boardId, query, canView && clockingAvailable);

  // Settings GET is manage-only; calling it with view-only roles trips the global 403 toast.
  const { settings, refresh: refreshSettings } = useClockingSettings(
    boardId,
    canManage
  );

  const actions = useClockingActions(boardId);

  const emptyModel = useMemo(
    () => ({
      dateRange: { start: range.startDate, end: range.endDate },
      dates: [],
      legend: [],
      groups: [],
    }),
    [range.startDate, range.endDate]
  );

  const guideLayoutKey =
    !attendanceLoading && (model?.groups.length ?? 0) === 0 ? "empty" : "grid";

  const { replayGuide } = useAutoPageGuide({
    viewType: VIEW_TYPES.clocking,
    enabled: Boolean(
      boardId && canView && clockingAvailable && !accessLoading && !attendanceLoading
    ),
    contextKey: guideLayoutKey,
  });

  const inboxItems = useMemo(() => buildExceptionInbox(model), [model]);
  const actionableExceptionCount = useMemo(
    () => countActionableExceptions(inboxItems),
    [inboxItems]
  );

  const openInbox = useCallback((date?: string | null) => {
    detailRequestRef.current += 1;
    setDetail(null);
    setSelection(null);
    setDetailLoading(false);
    setInboxDateFilter(date ?? null);
    setInboxOpen(true);
  }, []);

  const closeInbox = useCallback(() => {
    setInboxOpen(false);
    setInboxDateFilter(null);
  }, []);

  const handleCellSelect = useCallback(
    async (cell: ClockingCell, row: ClockingRow, group: ClockingGroup) => {
      const requestId = ++detailRequestRef.current;
      setInboxOpen(false);
      setInboxDateFilter(null);
      setSelection({
        attendanceId: cell.attendanceId,
        timeOffId: cell.timeOffId,
        resourceId: row.resourceId,
        date: cell.date,
        groupKey: group.key,
      });
      setDetail(null);
      setDetailLoading(true);

      try {
        if (cell.attendanceId != null) {
          const d = await actions.getAttendanceDetail(cell.attendanceId);
          if (requestId !== detailRequestRef.current) return;
          if (d) setDetail({ kind: "attendance", detail: d });
          return;
        }
        if (cell.timeOffId != null) {
          const d = await actions.getTimeOffDetail(cell.timeOffId);
          if (requestId !== detailRequestRef.current) return;
          if (d) setDetail({ kind: "timeOff", detail: d });
        }
      } finally {
        if (requestId === detailRequestRef.current) setDetailLoading(false);
      }
    },
    [actions]
  );

  const closeDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setDetail(null);
    setSelection(null);
    setDetailLoading(false);
  }, []);

  const openInboxItem = useCallback(
    async (attendanceId: number, resourceId: number, date: string) => {
      const requestId = ++detailRequestRef.current;
      setInboxOpen(false);
      setSelection({
        attendanceId,
        timeOffId: null,
        resourceId,
        date,
        groupKey: null,
      });
      setDetail(null);
      setDetailLoading(true);
      try {
        const d = await actions.getAttendanceDetail(attendanceId);
        if (requestId !== detailRequestRef.current) return;
        if (d) setDetail({ kind: "attendance", detail: d });
      } finally {
        if (requestId === detailRequestRef.current) setDetailLoading(false);
      }
    },
    [actions]
  );

  const applyBulkUpdates = useCallback(
    async (updated: AttendanceDetail[]) => {
      // Refresh can lag behind mutations; re-apply local patches so day Clear counts drop immediately.
      await refreshAttendance();
      for (const row of updated) applyAttendanceDetail(row);
    },
    [applyAttendanceDetail, refreshAttendance]
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (settingsOpen) return;
      if (inboxOpen) {
        closeInbox();
        return;
      }
      if (!detail && !detailLoading) return;
      closeDetail();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeDetail, closeInbox, detail, detailLoading, inboxOpen, settingsOpen]);

  const widenToFourteenDays = () => {
    const end = new Date();
    setRange({
      startDate: format(subDays(end, 13), "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
    });
    setExceptionsOnly(false);
  };

  if (!boardId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a board to open Clock.
      </div>
    );
  }

  if (accessLoading && !access) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Checking clocking access…
      </div>
    );
  }

  if (accessError && !access) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-sm font-medium text-foreground">Unable to load Clock</p>
        <p className="text-xs text-muted-foreground">{accessError}</p>
      </div>
    );
  }

  if (!canView) {
    if (canManage) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm font-medium text-foreground">Clocking is not available for review</p>
          <p className="max-w-md text-xs text-muted-foreground">
            You can open settings to enable clocking for this board.
          </p>
          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={() => setSettingsOpen(true)}
          >
            Open clocking settings
          </button>
          <ClockingSettingsSheet
            boardId={boardId}
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            onSaved={() => void refreshAccess()}
          />
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        You do not have permission to view Clock on this board.
      </div>
    );
  }

  if (!clockingAvailable) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm font-medium text-foreground">Clocking is turned off</p>
        <p className="max-w-md text-xs text-muted-foreground">
          {canManage
            ? "Enable clocking in settings to start reviewing attendance."
            : "Ask a board manager to enable clocking."}
        </p>
        {canManage ? (
          <>
            <button
              type="button"
              className="text-sm text-primary underline"
              onClick={() => setSettingsOpen(true)}
            >
              Open clocking settings
            </button>
            <ClockingSettingsSheet
              boardId={boardId}
              open={settingsOpen}
              onOpenChange={setSettingsOpen}
              onSaved={() => void refreshAccess()}
            />
          </>
        ) : null}
      </div>
    );
  }

  const gridModel = model ?? emptyModel;
  const showEmpty = !attendanceLoading && gridModel.groups.length === 0;
  const showSidePane = inboxOpen || Boolean(detail) || detailLoading;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
      <div className="shrink-0 border-b border-border px-3 py-2">
        <ClockingToolbar
          startDate={range.startDate}
          endDate={range.endDate}
          exceptionsOnly={exceptionsOnly}
          canManage={canManage}
          isRefreshing={attendanceLoading}
          incentivesEnabled={Boolean(settings?.incentives_enabled)}
          clockInPoints={settings?.clock_in_points}
          clockOutPoints={settings?.clock_out_points}
          missedClockPenalty={settings?.missed_clock_penalty}
          legend={gridModel.legend}
          exceptionCount={actionableExceptionCount}
          inboxOpen={inboxOpen}
          onRangeChange={(startDate, endDate) => setRange({ startDate, endDate })}
          onExceptionsOnlyChange={setExceptionsOnly}
          onRefresh={() => void refreshAttendance()}
          onOpenInbox={() => openInbox(null)}
          onOpenSettings={() => setSettingsOpen(true)}
          onShowGuide={replayGuide}
        />
        {attendanceError ? (
          <p className="mt-1.5 text-xs text-destructive">{attendanceError}</p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1">
        <ResizablePanelGroup orientation="horizontal" className="h-full min-h-0">
          <ResizablePanel id="clocking-grid" defaultSize={showSidePane ? "68%" : "100%"}>
            <div className="h-full overflow-hidden">
              {showEmpty ? (
                <ClockingEmptyState
                  exceptionsOnly={exceptionsOnly}
                  onShowAll={() => setExceptionsOnly(false)}
                  onWidenRange={widenToFourteenDays}
                />
              ) : (
                <CustomMatrixClockingGrid
                  model={gridModel}
                  selected={selection}
                  onCellSelect={(cell, row, group) => void handleCellSelect(cell, row, group)}
                  onDateHeaderClick={(date) => openInbox(date)}
                  isLoading={attendanceLoading}
                />
              )}
            </div>
          </ResizablePanel>

          {showSidePane ? (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel id="clocking-detail" defaultSize="32%" minSize={"22%"} maxSize={"50%"}>
                <div className="h-full overflow-hidden border-l border-border">
                  {inboxOpen ? (
                    <ExceptionInboxPanel
                      items={inboxItems}
                      dateFilter={inboxDateFilter}
                      canManage={canManage}
                      isActing={actions.isActing}
                      onClose={closeInbox}
                      onOpenItem={(item) =>
                        void openInboxItem(item.attendanceId, item.resourceId, item.date)
                      }
                      onBulkExcuse={async (ids) => {
                        const updated = await actions.bulkExcuse(ids);
                        await applyBulkUpdates(updated);
                      }}
                      onBulkMarkReviewed={async (ids) => {
                        const updated = await actions.bulkMarkReviewed(ids);
                        await applyBulkUpdates(updated);
                      }}
                    />
                  ) : detailLoading && !detail ? (
                    <DetailLoadingPane />
                  ) : detail?.kind === "attendance" ? (
                    <AttendanceDetailPanel
                      key={`att-${detail.detail.id}`}
                      detail={detail.detail}
                      canManage={canManage}
                      isActing={actions.isActing}
                      onClose={closeDetail}
                      onMarkReviewed={async (note) => {
                        const updated = await actions.markReviewed(detail.detail.id, note);
                        if (updated) {
                          setDetail({ kind: "attendance", detail: updated });
                          applyAttendanceDetail(updated);
                        }
                      }}
                      onExcuse={async (note) => {
                        const updated = await actions.excuse(detail.detail.id, note);
                        if (updated) {
                          setDetail({ kind: "attendance", detail: updated });
                          applyAttendanceDetail(updated);
                        }
                      }}
                      onAddNote={async (note) => {
                        const updated = await actions.addNote(detail.detail.id, note);
                        if (updated) {
                          setDetail({ kind: "attendance", detail: updated });
                          applyAttendanceDetail(updated);
                        }
                      }}
                      onCorrect={async (body) => {
                        const updated = await actions.correct(detail.detail.id, body);
                        if (updated) {
                          setDetail({ kind: "attendance", detail: updated });
                          applyAttendanceDetail(updated);
                        }
                      }}
                    />
                  ) : detail?.kind === "timeOff" ? (
                    <TimeOffDetailPanel
                      key={`to-${detail.detail.id}`}
                      detail={detail.detail}
                      canManage={canManage}
                      isActing={actions.isActing}
                      onClose={closeDetail}
                      onApprove={async (adminNotes) => {
                        const updated = await actions.patchTimeOff(detail.detail.id, {
                          approval_status: TIME_OFF_APPROVAL.APPROVED,
                          admin_notes: adminNotes,
                        });
                        if (updated) {
                          setDetail({ kind: "timeOff", detail: updated });
                          void refreshAttendance();
                        }
                      }}
                      onDeny={async (adminNotes) => {
                        const updated = await actions.patchTimeOff(detail.detail.id, {
                          approval_status: TIME_OFF_APPROVAL.DENIED,
                          admin_notes: adminNotes,
                        });
                        if (updated) {
                          setDetail({ kind: "timeOff", detail: updated });
                          void refreshAttendance();
                        }
                      }}
                    />
                  ) : (
                    <DetailLoadingPane />
                  )}
                </div>
              </ResizablePanel>
            </>
          ) : null}
        </ResizablePanelGroup>
      </div>

      {canManage ? (
        <ClockingSettingsSheet
          boardId={boardId}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          onSaved={() => {
            void refreshAccess();
            void refreshAttendance();
            void refreshSettings();
          }}
        />
      ) : null}
    </div>
  );
}
